/**
 * Centralny klient HTTP dla panelu urzędnika.
 *
 * Wszystkie wywołania idą przez Next.js proxy (/api/*) → NestJS (/api/*),
 * dzięki czemu ciasteczko httpOnly z refresh-tokenem jest obsługiwane
 * po stronie przeglądarki na tym samym origin (localhost:3000).
 *
 * Access-token (JWT) jest przechowywany w localStorage pod kluczem `szp_at`.
 */

const BASE = process.env.NEXT_PUBLIC_API_URL ?? '/api';

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('szp_at');
}

export function setAccessToken(token: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('szp_at', token);
}

export function clearAccessToken(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('szp_at');
}

function buildHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extra,
  };
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export async function apiFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const url = `${BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...buildHeaders(),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  // Jeśli 401 — spróbuj odświeżyć token
  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          ...buildHeaders(),
          ...(options.headers as Record<string, string> | undefined),
        },
      });
    }
    // Nie udało się odświeżyć → wyloguj
    clearAccessToken();
    if (typeof window !== 'undefined') {
      window.location.href = '/panel/login';
    }
  }

  return res;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data: { accessToken: string } = await res.json();
    setAccessToken(data.accessToken);
    return true;
  } catch {
    return false;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(path);
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || `POST ${path} → ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'PATCH',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || `PATCH ${path} → ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function apiDelete(path: string): Promise<void> {
  const res = await apiFetch(path, { method: 'DELETE' });
  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || `DELETE ${path} → ${res.status}`);
  }
}

/**
 * Upload pliku (FormData) — NIE ustawia Content-Type,
 * żeby przeglądarka mogła sama wyznaczyć multipart boundary.
 */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const url = `${BASE}${path}`;
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      const token2 = getAccessToken();
      const headers2: Record<string, string> = {};
      if (token2) headers2['Authorization'] = `Bearer ${token2}`;
      const res2 = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: headers2,
        body: formData,
      });
      if (!res2.ok) {
        const msg = await res2.text();
        throw new Error(msg || `POST ${path} → ${res2.status}`);
      }
      return res2.json() as Promise<T>;
    }
    clearAccessToken();
    if (typeof window !== 'undefined') window.location.href = '/panel/login';
  }

  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || `POST ${path} → ${res.status}`);
  }
  return res.json() as Promise<T>;
}
